import {
  Send, Calendar, Clock, CheckCircle2, Reply, Users, Briefcase,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { EngagementStatusBadge } from "@/components/campaign/EngagementStatusBadge";
import { campaignResultLabel } from "@/lib/campaign-result";
import type { ContactCampaign, ContactOpportunity } from "@/hooks/useContactIntelligence";

// ─────────────────────────────────────────────────────────────────────────────
// ContactActivityTab — the READ-ONLY campaign history for the standardized
// contact modal. Lists each campaign the contact was in and the RESULT in plain
// language ("Accepted — no reply", "Replied — interested", "Bounced"). It
// deliberately shows NO message bodies, generation context, or reply text —
// that lives only in the campaign/task comms modal. Opportunities (name + role)
// are listed too, with no message content. (Standard: contact_modal.md §3.)
// ─────────────────────────────────────────────────────────────────────────────

interface ContactActivityTabProps {
  campaigns: ContactCampaign[] | undefined;
  opportunities?: ContactOpportunity[] | undefined;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
}

function CampaignSummaryBar({ campaigns }: { campaigns: ContactCampaign[] }) {
  const total = campaigns.length;
  const replied = campaigns.filter((c) => c.engagement_status === "replied" || c.engagement_status === "meeting_booked").length;
  const accepted = campaigns.filter((c) => c.engagement_status === "connected").length;
  const sent = campaigns.filter((c) => c.engagement_status === "sent").length;

  return (
    <div className="grid grid-cols-4 gap-3">
      {[
        { n: total, label: "Campaigns" },
        { n: replied, label: "Replied" },
        { n: accepted, label: "Accepted" },
        { n: sent, label: "Sent" },
      ].map((s) => (
        <div key={s.label} className="text-center p-2 bg-muted/50 rounded-lg">
          <p className="text-lg font-bold">{s.n}</p>
          <p className="text-[10px] text-muted-foreground">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

function CampaignEntry({ campaign }: { campaign: ContactCampaign }) {
  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Send className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium truncate">{campaign.campaign_name}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <EngagementStatusBadge status={campaign.engagement_status || "planned"} />
          {campaign.reply_sentiment && (
            <Badge variant="secondary" className="text-[11px] h-4 px-1">{campaign.reply_sentiment}</Badge>
          )}
        </div>
      </div>

      {/* Result — the plain-English outcome (no message content). */}
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Result:</span>
        <span className="text-sm font-medium">{campaignResultLabel(campaign)}</span>
      </div>

      {/* Timeline dots */}
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground flex-wrap">
        {campaign.enrolled_at && (
          <span className="flex items-center gap-0.5">
            <Clock className="h-2.5 w-2.5" />
            Enrolled {formatDate(campaign.enrolled_at)}
          </span>
        )}
        {campaign.sent_at && (
          <>
            <span>→</span>
            <span className="flex items-center gap-0.5">
              <Send className="h-2.5 w-2.5" />
              Sent {formatDate(campaign.sent_at)}
            </span>
          </>
        )}
        {campaign.accepted_at && (
          <>
            <span>→</span>
            <span className="flex items-center gap-0.5">
              <CheckCircle2 className="h-2.5 w-2.5" />
              Accepted {formatDate(campaign.accepted_at)}
            </span>
          </>
        )}
        {campaign.replied_at && (
          <>
            <span>→</span>
            <span className="flex items-center gap-0.5">
              <Reply className="h-2.5 w-2.5" />
              Replied {formatDate(campaign.replied_at)}
            </span>
          </>
        )}
      </div>

      {campaign.meeting_booked && (
        <div className="flex items-center gap-1 text-xs">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <span>Meeting booked</span>
        </div>
      )}
    </div>
  );
}

export function ContactActivityTab({ campaigns, opportunities }: ContactActivityTabProps) {
  const camps = campaigns ?? [];
  const opps = opportunities ?? [];

  if (camps.length === 0 && opps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Users className="h-10 w-10 mb-3 opacity-30" />
        <p className="text-sm font-medium">No campaign history</p>
        <p className="text-xs mt-1">Campaign enrollments and opportunities will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {camps.length > 0 && <CampaignSummaryBar campaigns={camps} />}

      {camps.length > 0 && (
        <div className="space-y-3">
          {camps.map((c) => (
            <CampaignEntry key={c.id} campaign={c} />
          ))}
        </div>
      )}

      {opps.length > 0 && (
        <>
          <Separator />
          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Briefcase className="h-3.5 w-3.5" />
              Opportunities ({opps.length})
            </h3>
            <div className="space-y-1.5">
              {opps.map((opp) => (
                <div key={opp.id} className="flex items-center justify-between text-sm border rounded-md p-2">
                  <div>
                    <div className="font-medium text-xs">{opp.opportunity_name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {[opp.buying_role, opp.relationship_strength].filter(Boolean).join(" · ") || "Associated"}
                    </div>
                  </div>
                  {opp.is_primary && (
                    <Badge variant="default" className="text-[11px] h-4 px-1">Primary</Badge>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
